/**
 * landing.js — landing page behaviors (dependency-free vanilla JS).
 *
 * 1. Desktop (xl+, min-width 1280px) pinned-hero scroll choreography — port of
 *    features/landing/scroll-animation-wrapper.tsx. The theme renders the
 *    sections ONCE (sequential flow); on xl+ this script pins the hero
 *    section's .shopify-section wrapper as a fixed full-viewport layer,
 *    inserts a spacer (viewport height * 1.08, the source's heroHeight), and
 *    raises the featured/products section (z-20) and everything after it
 *    (z-10) so they slide over the hero. On scroll the hero fades
 *    (opacity = 1 - progress * 0.5) and is visibility-hidden once scrolled
 *    past — exactly the source math. Below xl (and without JS) the page is
 *    the plain sequential mobile layout.
 * 2. Animated stat counters — port of UGC_section.tsx AnimatedStatCard
 *    (IntersectionObserver at 0.3 threshold, 1.5s easeOutCubic count-up with
 *    per-card delays, progress bar width tracks the value).
 * 3. Mobile stats rail centering — centers the middle stat card on load.
 * 4. About-video play/pause toggle — [data-video-toggle] inside
 *    [data-video-frame], with play/pause icon + aria-label sync.
 */
(function () {
  "use strict";

  var XL_QUERY = "(min-width: 1280px)";
  var reducedMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function onReady(fn) {
    if (window.themeUtils && typeof window.themeUtils.onReady === "function") {
      window.themeUtils.onReady(fn);
    } else if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn, { once: true });
    } else {
      fn();
    }
  }

  /* ------------------------------------------------------------------ */
  /* 1. Pinned-hero scroll choreography (xl+)                            */
  /* ------------------------------------------------------------------ */
  function initHeroChoreography() {
    var hero = document.querySelector("[data-landing-hero]");
    if (!hero || hero.__landingPinned) return;
    hero.__landingPinned = true;

    var heroWrap = hero.closest(".shopify-section") || hero;
    var featured = document.querySelector("[data-landing-featured]");
    var featuredWrap = featured
      ? featured.closest(".shopify-section") || featured
      : null;

    var mq = window.matchMedia(XL_QUERY);
    var active = false;
    var spacer = null;
    var styledSiblings = [];
    var heroHeight = 0;
    var ticking = false;
    var heroVisible = true;

    function applyHeights() {
      var vh = window.innerHeight;
      heroHeight = Math.round(vh * 1.08);
      heroWrap.style.height = vh + "px";
      if (spacer) spacer.style.height = heroHeight + "px";
    }

    function update() {
      var scrollY = window.scrollY;
      var progress = Math.min(scrollY / heroHeight, 1);
      heroWrap.style.opacity = String(1 - progress * 0.5);
      var shouldBeVisible = progress < 1;
      if (shouldBeVisible !== heroVisible) {
        heroVisible = shouldBeVisible;
        heroWrap.style.visibility = shouldBeVisible ? "visible" : "hidden";
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        if (active) update();
        ticking = false;
      });
    }

    function setup() {
      if (active) return;
      active = true;
      heroVisible = true;

      spacer = document.createElement("div");
      spacer.setAttribute("data-landing-hero-spacer", "");
      spacer.setAttribute("aria-hidden", "true");
      heroWrap.parentNode.insertBefore(spacer, heroWrap.nextSibling);

      applyHeights();

      // Fixed hero layer (pointer-events re-enabled by the hero's own
      // pointer-events-auto content groups, same as the source wrapper).
      heroWrap.style.position = "fixed";
      heroWrap.style.top = "0";
      heroWrap.style.left = "0";
      heroWrap.style.right = "0";
      heroWrap.style.width = "100%";
      heroWrap.style.zIndex = "10";
      heroWrap.style.pointerEvents = "none";
      heroWrap.style.willChange = "opacity";

      // Featured section slides over the hero (z-20); everything after it
      // (remaining landing sections + footer group) stacks at z-10, painted
      // above the fixed hero because it comes later in the DOM.
      if (featuredWrap) {
        featuredWrap.style.position = "relative";
        featuredWrap.style.zIndex = "20";
        styledSiblings.push(featuredWrap);
      }
      var node = (featuredWrap || spacer).nextElementSibling;
      while (node) {
        if (node !== spacer && node.nodeType === 1) {
          node.style.position = "relative";
          node.style.zIndex = "10";
          styledSiblings.push(node);
        }
        node = node.nextElementSibling;
      }

      window.addEventListener("scroll", onScroll, { passive: true });
      update();
    }

    function teardown() {
      if (!active) return;
      active = false;
      window.removeEventListener("scroll", onScroll);
      if (spacer && spacer.parentNode) spacer.parentNode.removeChild(spacer);
      spacer = null;
      heroWrap.style.cssText = "";
      styledSiblings.forEach(function (el) {
        el.style.position = "";
        el.style.zIndex = "";
      });
      styledSiblings = [];
    }

    function sync() {
      if (mq.matches) {
        setup();
      } else {
        teardown();
      }
    }

    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", sync);
    } else if (typeof mq.addListener === "function") {
      mq.addListener(sync);
    }

    // Source only re-measured on orientationchange (debounced 100ms) — never
    // on scroll/resize — to avoid mobile URL-bar jumps.
    var orientationTimer = null;
    window.addEventListener("orientationchange", function () {
      if (orientationTimer) clearTimeout(orientationTimer);
      orientationTimer = setTimeout(function () {
        if (active) {
          applyHeights();
          update();
        }
      }, 100);
    });

    sync();
  }

  /* ------------------------------------------------------------------ */
  /* 2. Animated stat counters                                           */
  /* ------------------------------------------------------------------ */
  function animateCounter(card) {
    if (card.__statAnimated) return;
    card.__statAnimated = true;

    var target = parseInt(card.getAttribute("data-stat-counter"), 10) || 0;
    var delay = parseInt(card.getAttribute("data-stat-delay"), 10) || 0;
    var valueEl = card.querySelector("[data-stat-value]");
    var barEl = card.querySelector("[data-stat-bar]");

    function paint(value) {
      if (valueEl) valueEl.textContent = value + "%";
      if (barEl) barEl.style.width = value + "%";
    }

    if (reducedMotion) {
      paint(target);
      return;
    }

    setTimeout(function () {
      var duration = 1500;
      var startTime = performance.now();

      function step(now) {
        var elapsed = now - startTime;
        var progress = Math.min(elapsed / duration, 1);
        // Ease out cubic for smooth deceleration (same as source)
        var easeOutCubic = 1 - Math.pow(1 - progress, 3);
        paint(Math.round(target * easeOutCubic));
        if (progress < 1) window.requestAnimationFrame(step);
      }

      window.requestAnimationFrame(step);
    }, delay);
  }

  function initStatCounters() {
    var sections = document.querySelectorAll("[data-stats-section]");
    if (!sections.length) return;

    if (!("IntersectionObserver" in window)) {
      sections.forEach(function (section) {
        section
          .querySelectorAll("[data-stat-counter]")
          .forEach(animateCounter);
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target
            .querySelectorAll("[data-stat-counter]")
            .forEach(animateCounter);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.3, rootMargin: "0px" },
    );

    sections.forEach(function (section) {
      io.observe(section);
    });
  }

  /* ------------------------------------------------------------------ */
  /* 3. Mobile stats rail — center the middle card on load               */
  /* ------------------------------------------------------------------ */
  function centerStatsRails() {
    if (window.matchMedia(XL_QUERY).matches) return;
    document.querySelectorAll("[data-stats-scroll]").forEach(function (rail) {
      var middle = rail.querySelector("[data-stats-middle]");
      if (!middle) return;
      // Double rAF so layout is settled (same as the source effect)
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          var left =
            middle.offsetLeft - rail.offsetWidth / 2 + middle.offsetWidth / 2;
          rail.scrollTo({ left: left, behavior: "auto" });
        });
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* 4. Video play/pause toggle ([data-video-frame] + [data-video-toggle])*/
  /* ------------------------------------------------------------------ */
  function syncToggle(video) {
    var frame = video.closest("[data-video-frame]");
    if (!frame) return;
    var btn = frame.querySelector("[data-video-toggle]");
    if (!btn) return;
    var playIcon = btn.querySelector("[data-icon-play]");
    var pauseIcon = btn.querySelector("[data-icon-pause]");
    var playing = !video.paused && !video.ended;
    if (playIcon) playIcon.classList.toggle("hidden", playing);
    if (pauseIcon) pauseIcon.classList.toggle("hidden", !playing);
    var label = playing
      ? btn.getAttribute("data-label-pause")
      : btn.getAttribute("data-label-play");
    if (label) btn.setAttribute("aria-label", label);
  }

  function initVideoToggles() {
    document.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-video-toggle]");
      if (!btn) return;
      var frame = btn.closest("[data-video-frame]");
      var video = frame ? frame.querySelector("video") : null;
      if (!video) return;
      if (video.paused) {
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      } else {
        video.pause();
      }
    });

    // Media events don't bubble — listen in capture phase.
    document.addEventListener(
      "play",
      function (e) {
        if (e.target && e.target.tagName === "VIDEO") syncToggle(e.target);
      },
      true,
    );
    document.addEventListener(
      "pause",
      function (e) {
        if (e.target && e.target.tagName === "VIDEO") syncToggle(e.target);
      },
      true,
    );
  }

  onReady(function () {
    initHeroChoreography();
    initStatCounters();
    centerStatsRails();
    initVideoToggles();
  });

  // Theme editor: re-run the content-reveal initializers when a section is
  // re-rendered (called by theme.js on shopify:section:load). The pinned-hero
  // choreography is intentionally NOT re-run here — it rewires global page
  // flow (spacer + sibling z-index), not a single section, and is left to a
  // full preview reload. Idempotent via card.__statAnimated.
  window.themeLanding = {
    scan: function () {
      initStatCounters();
      centerStatsRails();
    },
  };
})();
